export type LlamoxPlan = "free" | "pro" | "enterprise";

export type LlamoxBillingInterval = "monthly" | "annual";

export type LlamoxSubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "none";

export interface WorkspaceLimits {
  applications?: number;
  apps?: number;
  storage?: number;
  executions?: number;
  apiCalls?: number;
  api_calls?: number;
}

export interface WorkspaceFeatures {
  [key: string]: boolean | string | number;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: LlamoxPlan;
  owner?: string;
  clerkOrgId?: string | null;
  limits?: WorkspaceLimits;
  features?: WorkspaceFeatures;
  memberCount?: number;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripePriceId?: string | null;
  /**
   * Period end in seconds (Stripe convention). Sourced from the Convex
   * overlay when available; not present on the REST `GET /workspaces`
   * payload today.
   */
  currentPeriodEnd?: number;
  // Optional fields populated from the Convex live overlay (see
  // app/providers/WorkspaceProvider.tsx). Absent in REST-only mode.
  description?: string;
  logoUrl?: string;
  lastActivity?: number;
  deletionStatus?: "active" | "deleting";
}

export interface ListWorkspacesResponse {
  success?: boolean;
  workspaces: Workspace[];
  total?: number;
  totalCount?: number;
}

export interface CreateWorkspaceResponse {
  success?: boolean;
  workspace: Workspace;
}

export interface WorkspaceMemberPermissions {
  canManageBilling?: boolean;
  canManageMembers?: boolean;
  canManageSettings?: boolean;
  [key: string]: boolean | undefined;
}

export interface WorkspaceMember {
  id?: string;
  userId?: string;
  email?: string;
  name?: string;
  role: "owner" | "admin" | "member" | "viewer" | string;
  joinedAt?: string;
  permissions?: WorkspaceMemberPermissions;
}

export interface ListMembersResponse {
  success?: boolean;
  members: WorkspaceMember[];
  currentMember?: WorkspaceMember;
  invitations?: unknown[];
  total?: number;
}

export interface CheckoutResponse {
  url: string;
  sessionId?: string;
}

export interface PortalResponse {
  url: string;
}

export interface SubscriptionResponse {
  plan: LlamoxPlan;
  subscription?: {
    id?: string;
    status: LlamoxSubscriptionStatus;
    interval?: LlamoxBillingInterval | "month" | "year";
    currentPeriodStart?: number;
    currentPeriodEnd?: number;
    cancelAtPeriodEnd?: boolean;
    amount?: number;
    currency?: string;
  } | null;
}

export interface CancelResponse {
  success: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd?: number;
}

export class LlamoxApiError extends Error {
  status: number;
  code?: string;
  body: unknown;

  constructor(status: number, message: string, body: unknown, code?: string) {
    super(message);
    this.name = "LlamoxApiError";
    this.status = status;
    this.body = body;
    this.code = code;
  }
}
